import React, { forwardRef, ForwardRefRenderFunction } from "react";
import { ThunkDispatch } from "redux-thunk";
import { useDispatch, useSelector } from "react-redux";
import { loadCompanyDataAsync } from "store/actions/authActions";
import { AppState } from "store";
//#region types
import { CustomerFormTypes, TimeReportingProjectFormTypes } from "models/formTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { TimeReportingProjectFormContainerProps } from "models/propTypes/timeReportingProjectPropTypes";
import { WintSuperkollAPIModel, WintSuperkollAPIModelTimeReporting as ApiModel } from "wint-api-client/dist/types";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { SelectionItem } from "models/utilTypes/selectionTypes";
import { TranslationKeys } from "translations";
import { FormFieldConstants, IconDefinitionConstants, SalaryDeviationConstants } from "utils/constants";
import { FormikProps } from "formik";
import { CurrencyEnums, DialogEnums, StyleEnums } from "models/enums";
import { displayDialog } from "store/actions/utilActions";
//#endregion
//#region  components
import ActiveCategoryHelperInstance from "../activeCategoryHelper";
import NumberOfProjectCategoryMembers from "../numberOfProjectCategoryMembers";
import TimeReportingProjectForm from "./timeReportingProjectForm";
import useTimeReportingHelpers from "./useTimeReportingHelpers";
import { findSelfBillingMembersMissingRateForNewCategories } from "./selfBillingValidation";
import { ReferencePersonFormContainer, useCreateCustomerDialog } from "components/customer";
import { useFloatingMessages, useWizardContext } from "components/common";
import {
  useInvoiceService,
  useDimensionService,
  useCustomerService,
  useTimeReportingProjectService,
} from "serviceHooks";
import { useSalaryPermissions } from "components/salary";
//#endregion
import { SalaryDeviationsService } from "services";
import { LocalizationHelperInstance, EnumHelper, DateHelper } from "utils/helpers";
import * as Yup from "yup";

const TimeReportingProjectFormContainer: ForwardRefRenderFunction<
  FormHandles,
  TimeReportingProjectFormContainerProps
> = (props, ref) => {
  const timeReportingProjectService = useTimeReportingProjectService();
  const customerService = useCustomerService();
  const invoiceService = useInvoiceService();
  const dimensionService = useDimensionService();

  const formRef = React.useRef<FormHandles>(null);
  React.useImperativeHandle(ref, () => (formRef?.current ? formRef.current : ({} as FormHandles)));

  const { selectedTimeReportingProjectChangeHandler, timeReportingProject } = props;
  const [topCustomers, setTopCustomers] = React.useState<AutocompleteListItem[]>([]);
  const [topReferencePersons, setTopReferencePersons] = React.useState<AutocompleteListItem[]>([]);
  const [selectedCustomer, setSelectedCustomer] = React.useState<number | undefined>(
    props.timeReportingProject?.customerId
  );
  const [isProjectNameUnique, setIsProjectNameUnique] = React.useState<boolean>(true);

  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const isCreate = !timeReportingProject?.id;
  const originalProjectName = timeReportingProject?.name;
  const [currency, setCurrency] = React.useState<string>(CurrencyEnums.CurrencyCode.SEK);
  const { enqueueMessage } = useFloatingMessages();
  const wizardContext = useWizardContext();

  const [loggedInUser] = useSelector((state: AppState) => [state.auth.loggedInUser]);

  const { isSalaryDeviationsAvailable } = useSalaryPermissions();
  const allowedDeviationTypes = useSelector((state: AppState) => state.auth.selectedCompany.allowedDeviationTypes);

  const enabledOtherDeviationTypes = SalaryDeviationConstants.OtherDeviationTypes.filter((value) =>
    allowedDeviationTypes?.includes(value)
  );

  React.useEffect(() => {
    customerService.getCustomerSuggestions().then((res) => {
      if (res && res.length > 0) {
        setTopCustomers(
          res.map(
            (item): AutocompleteListItem => ({
              id: item.id ?? 0,
              value: item.name ?? "",
              icon: IconDefinitionConstants.WBuilding,
              group: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Suggestions),
            })
          )
        );
      } else {
        setTopCustomers([]);
      }
    });
    setIsProjectNameUnique(true);

    if (isCreate) {
      dimensionService.getInvoiceDimensionTypes().then((res) => {
        const sellerDimension = res.find((d) => d.type === WintSuperkollAPIModel.DimensionCompatibilityType.Seller);
        sellerDimension &&
          formRef.current?.setFieldValue &&
          formRef.current?.setFieldValue(
            FormFieldConstants.TimeReportingProject.Create.HasSellerDimensionInInvoice,
            true
          );
      });
    }
  }, []);

  React.useEffect(() => {
    if (timeReportingProject) {
      setCurrency(timeReportingProject.currency ?? CurrencyEnums.CurrencyCode.SEK);
      if (timeReportingProject.customerId) {
        setReferencePersonSuggestions(timeReportingProject.customerId);
      }
    }
  }, [timeReportingProject]);

  const { isMemberActive } = useTimeReportingHelpers();

  const mapSelfBillingRatesFromApi = (rates?: ApiModel.SelfBillingRateItem[]) =>
    rates?.map(
      (rate) =>
        ({
          id: rate.id,
          timeReportingProjectCategoryId: rate.timeReportingProjectCategoryId,
          categoryName:
            timeReportingProject?.projectCategories?.find((c) => c.id === rate.timeReportingProjectCategoryId)?.name ??
            "",
          amount: rate.amount,
          fromDate: rate.fromDate ? new Date(rate.fromDate) : undefined,
          isCurrent: rate.isCurrent,
        }) as TimeReportingProjectFormTypes.SelfBillingRate
    );

  const initialValues: TimeReportingProjectFormTypes.Create = {
    projectName: timeReportingProject?.name,
    projectDescription: timeReportingProject?.description,
    customerId: timeReportingProject?.customerId,
    customerName: timeReportingProject?.customerName,
    projectStartDate:
      timeReportingProject?.startDate ??
      DateHelper.resetTime(DateHelper.getFirstDayOfMonth(new Date())) ??
      DateHelper.today(),
    projectEndDate: timeReportingProject?.endDate,
    invoiceReferenceId: timeReportingProject?.invoiceReferencePersonId,
    invoiceReferenceName: timeReportingProject?.invoiceReferenceName,
    projectManagerId: timeReportingProject?.projectManagerPersonId ?? loggedInUser.personId,
    projectManagerName: timeReportingProject?.projectManagerName ?? loggedInUser.name,
    isAutoInvoicing: timeReportingProject?.isAutoInvoicing ?? true,
    isBundledInvoice: timeReportingProject?.isBundledInvoice ?? false,
    invoicingInterval: timeReportingProject?.invoicingInterval ?? ApiModel.TimeReportingInvoicingInterval.Weekly,
    invoicingIntervalName: LocalizationHelperInstance.getTranslatedEnumValue(
      "TimeReportingProject.InvoicingInterval",
      timeReportingProject?.invoicingInterval ?? ApiModel.InvoiceTimeReportDetailLevel.Daily
    ),
    includeCommentsOnAttachments: timeReportingProject?.includeCommentsOnAttachments ?? false,
    includeAttachmentsOnInvoice: timeReportingProject?.includeAttachmentsOnInvoice ?? false,
    currency: timeReportingProject?.currency ?? CurrencyEnums.CurrencyCode.SEK,
    invoiceTimeReportDetailLevel:
      timeReportingProject?.invoiceTimeReportDetailLevel ?? ApiModel.InvoiceTimeReportDetailLevel.Bundled,
    isDescriptionMandatory: timeReportingProject?.isDescriptionMandatory ?? false,
    periodisationEnabled: timeReportingProject?.periodisationEnabled ?? true,
    projectMembers: timeReportingProject?.projectMembers?.map(
      (p) =>
        ({
          id: p.id,
          name: p.name,
          isNew: false,
          startDate: p.startDate,
          endDate: p.endDate,
          isTimeReportingActive: isMemberActive(
            timeReportingProject.startDate,
            timeReportingProject.endDate,
            p.startDate,
            p.endDate
          ),
          selfBillingEnabled: p.selfBillingEnabled,
          selfBillingOrgNumber: p.selfBillingOrgNumber,
          selfBillingEmail: p.selfBillingEmail,
          selfBillingPaymentMethod: p.selfBillingPaymentMethod,
          selfBillingBankgiro: p.selfBillingBankgiro,
          selfBillingPlusgiro: p.selfBillingPlusgiro,
          selfBillingClearingNumber: p.selfBillingClearingNumber,
          selfBillingAccountNumber: p.selfBillingAccountNumber,
          selfBillingIBAN: p.selfBillingIBAN,
          selfBillingPaymentTerms: p.selfBillingPaymentTerms,
          selfBillingCalculationMethod: p.selfBillingCalculationMethod,
          selfBillingPercentage: p.selfBillingPercentage,
          selfBillingMonthlyCost: p.selfBillingMonthlyCost,
          // A category holds a rate history now, so fromDate and isCurrent have to come along:
          // they decide which row the member form shows as the rate in force, which rows are
          // queued changes, and they must be sent back unchanged for rows nobody touched.
          selfBillingHourlyRates: mapSelfBillingRatesFromApi(p.selfBillingHourlyRates),
          selfBillingAllHourlyRates: mapSelfBillingRatesFromApi(p.selfBillingHourlyRates),
        }) as TimeReportingProjectFormTypes.ProjectMember
    ),
    projectCategories:
      timeReportingProject?.projectCategories && timeReportingProject?.projectCategories.length > 0
        ? timeReportingProject?.projectCategories?.map(
            (cat) =>
              ({
                id: cat.id,
                name: cat.name,
                isDefault: cat.isDefault,
                defaultHourlyRate: cat.defaultHourlyRate,
                billingModel: cat.billingModel,
                isCatgoryActive: ActiveCategoryHelperInstance.isCategoryActive(cat),
                endDate: cat.endDate,
                connectedDeviationType: cat.connectedDeviationType,
                connectedDeviationTypeName:
                  cat.connectedDeviationType &&
                  SalaryDeviationsService.getSalaryDeviationTypeText(
                    cat.connectedDeviationType,
                    cat.customDeviationName
                  ),
                deviationComment: cat.deviationComment,
                customDeviationName: cat.customDeviationName,
                shouldEffectSalaryPayout: cat.shouldEffectSalaryPayout,
                adjustPreviouslyReportedTime: cat.adjustPreviouslyReportedTime,
              }) as TimeReportingProjectFormTypes.ProjectCategory
          )
        : [
            {
              name: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReportingProject.NormalTime),
              isDefault: true,
              billingModel: ApiModel.BillingModelType.Individual,
              defaultHourlyRate: 0,
              isCatgoryActive: true,
            } as TimeReportingProjectFormTypes.ProjectCategory,
          ],
    projectCategoryMembers: timeReportingProject?.projectCategoryMembers ?? [],
    isInternalProject: timeReportingProject?.customerId === undefined && !isCreate,
    loadingCustomer: timeReportingProject?.customerId === undefined,
    dimensionReadonly: timeReportingProject?.name,
    hasSellerDimensionInInvoice: timeReportingProject?.hasSellerDimensionInInvoice ?? false,
  };

  const validationSchema = Yup.object<TimeReportingProjectFormTypes.Create>({
    projectName: Yup.string()
      .max(100)
      .required(TranslationKeys.Validations.Required)
      .test("unique", TranslationKeys.TimeReportingProject.ProjectNameNotUnique, function () {
        return isProjectNameUnique;
      }),
    projectDescription: Yup.string(),
    customerId: Yup.number().when(
      FormFieldConstants.TimeReportingProject.Create.IsInternalProject,
      ([isInternalProject], schema: Yup.NumberSchema) =>
        isInternalProject ? schema : schema.required(TranslationKeys.Validations.Required)
    ),
    customerName: Yup.string().when(
      FormFieldConstants.TimeReportingProject.Create.IsInternalProject,
      ([isInternalProject], schema: Yup.StringSchema) =>
        isInternalProject ? schema : schema.required(TranslationKeys.Validations.Required)
    ),
    invoiceReferenceId: Yup.number(),
    invoiceReferenceName: Yup.string(),
    projectManagerId: Yup.number().required(TranslationKeys.Validations.Required),
    projectManagerName: Yup.string().required(TranslationKeys.Validations.Required),
    isAutoInvoicing: Yup.boolean(),
    projectStartDate: Yup.date()
      .typeError(TranslationKeys.Validations.InvalidDate)
      .required(TranslationKeys.Validations.Required),
    projectEndDate: Yup.date()
      .typeError(TranslationKeys.Validations.InvalidDate)
      .when(FormFieldConstants.TimeReportingProject.Create.ProjectStartDate, ([startDate], schema: Yup.DateSchema) =>
        startDate instanceof Date && !isNaN(startDate.getTime())
          ? schema.min(startDate, TranslationKeys.Validations.EndDateMustBeLaterThanStartDate)
          : schema
      ),
    isBundledInvoice: Yup.boolean(),
    invoicingInterval: Yup.number().required(TranslationKeys.Validations.Required),
    invoicingIntervalName: Yup.string().required(TranslationKeys.Validations.Required),
    includeCommentsOnAttachments: Yup.boolean(),
    includeAttachmentsOnInvoice: Yup.boolean(),
    currency: Yup.string().required(TranslationKeys.Validations.Required),
    invoiceTimeReportDetailLevel: Yup.number().required(TranslationKeys.Validations.Required),
    isDescriptionMandatory: Yup.boolean(),
    periodisationEnabled: Yup.boolean(),
    projectMembers: Yup.array(),
    isInternalProject: Yup.boolean(),
    loadingCustomer: Yup.boolean(),
    dimensionReadonly: Yup.string(),
    hasSellerDimensionInInvoice: Yup.boolean(),
    projectCategories: Yup.array(),
    projectCategoryMembers: Yup.array(),
  });

  const getInvoiceTimeReportDetailLevels = () => {
    const invoiceTimeReportDetailLevels = EnumHelper.getNumericItemValues(ApiModel.InvoiceTimeReportDetailLevel).map(
      (item) =>
        ({
          id: item,
          value: LocalizationHelperInstance.getTranslatedEnumValue(
            "TimeReportingProject.InvoiceTimeReportDetailLevel",
            item
          ),
        }) as SelectionItem
    );
    return invoiceTimeReportDetailLevels;
  };

  const onCustomerAdd = (name: string) => {
    showPreviewDialog(name, onCustomerSelected);
  };

  const onCustomerSelectedFromForm = (
    item: AutocompleteListItem | SelectionItem,
    name: string,
    formikProps: FormikProps<TimeReportingProjectFormTypes.Create>
  ) => {
    if (!!item.id) {
      onCustomerSelected(Number(item?.id), item?.value);
    }
  };

  const onCustomerSelected = (customerId?: number, customerName?: string) => {
    if (customerId !== selectedCustomer) {
      setCustomerLoading(true);

      const setValues = formRef.current?.setValues;
      const values: TimeReportingProjectFormTypes.Create = formRef.current?.getValues && formRef.current.getValues();
      setValues &&
        setValues<TimeReportingProjectFormTypes.Create>({
          ...values,
          customerId: customerId,
          customerName: customerName ?? values.customerName,
          invoiceReferenceId: undefined,
          invoiceReferenceName: undefined,
        });

      setSelectedCustomer(Number(customerId));
      setReferencePersonSuggestions(Number(customerId));
    }
  };

  const setCustomerLoading = (isLoading: boolean) => {
    formRef.current?.setFieldValue &&
      formRef.current.setFieldValue(FormFieldConstants.TimeReportingProject.Create.LoadingCustomer, isLoading);
  };

  const setReferencePersonSuggestions = (customerId: number) => {
    if (isNaN(customerId)) {
      setTopReferencePersons([]);
      return;
    }

    invoiceService
      .getReferencePersonSuggestions(customerId)
      .then((referenceResult) => {
        const topReferencePersons = referenceResult
          ? referenceResult
              .filter((reference) => reference.name)
              .map(
                (reference): AutocompleteListItem => ({
                  id: reference.id ?? 0,
                  value: reference.name ?? "",
                  data: reference,
                  icon: IconDefinitionConstants.WUser,
                })
              )
          : [];
        if (topReferencePersons.length > 0) {
          setTopReferencePersons(topReferencePersons);
        } else {
          setTopReferencePersons([]);
        }
      })
      .finally(() => {
        setCustomerLoading(false);
      });
  };

  const onAddReferencePerson = (name: string) => {
    const values: TimeReportingProjectFormTypes.Create = formRef.current?.getValues && formRef.current?.getValues();
    dispatch(
      displayDialog({
        isOpen: false,
        title: `${LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.Common.AddEntity
        )} ${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Person).toLowerCase()}`,
        titleIcon: IconDefinitionConstants.WBusinessCard,
        dialogType: DialogEnums.DialogType.Content,
        actions: [
          {
            text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.SaveChanges),
            successMessage: TranslationKeys.Invoice.ReferencePersonUpdatedSuccessMessage,
          },
        ],
        children: (
          <ReferencePersonFormContainer
            customerId={values.customerId}
            name={name}
            handleSubmit={(values: CustomerFormTypes.ReferencePerson) => onReferencePersonFormSubmit(values)}
          />
        ),
        maxWidth: "sm",
      })
    );
  };

  const onReferencePersonFormSubmit = async (referencePerson: CustomerFormTypes.ReferencePerson) => {
    if (formRef.current?.setFieldValue) {
      formRef.current?.setFieldValue(
        FormFieldConstants.TimeReportingProject.Create.InvoiceReferenceId,
        referencePerson.id
      );
      formRef.current?.setFieldValue(
        FormFieldConstants.TimeReportingProject.Create.InvoiceReferenceName,
        referencePerson.name
      );
    }
  };

  const onIsInternalProjectChange = (
    name: string,
    updatedValue: any,
    formikProps: FormikProps<any>,
    userInput?: boolean | undefined
  ) => {
    if (updatedValue && formRef.current?.setValues) {
      const updatedValues: TimeReportingProjectFormTypes.Create = {
        ...formikProps.values,
        customerId: undefined,
        customerName: "",
        invoiceReferenceId: undefined,
        invoiceReferenceName: "",
      };
      formRef.current.setValues(updatedValues);
    }
  };

  const handleSubmit = (values: TimeReportingProjectFormTypes.Create) => {
    const missingMemberNames = findSelfBillingMembersMissingRateForNewCategories(values);
    if (missingMemberNames.length > 0) {
      wizardContext?.setCurrentStep(2); // Members step
      // autoHide=undefined → persists; excludeFromDismissList so wizard step clicks don't kill it.
      enqueueMessage(
        LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.TimeReportingProject.SelfBillingMissingHourlyRateForCategory,
          undefined,
          { names: missingMemberNames.join(", ") }
        ),
        StyleEnums.DisplayType.Danger,
        undefined,
        true,
        { excludeFromDismissList: true }
      );
      // 1-char placeholder triggers the wizard's hasErrors styling on the Members step but is
      // hidden from field-level rendering by the escape hatch in formFieldWrapper.tsx:37.
      formRef.current?.setFieldError?.(FormFieldConstants.TimeReportingProject.Create.ProjectMembers, "!");
      return Promise.reject(new Error("self-billing validation failed"));
    }

    return createOrUpdateProject(values).then((res) => {
      const isSellerDimensionFieldChanged =
        initialValues.hasSellerDimensionInInvoice !== values.hasSellerDimensionInInvoice;
      if (isSellerDimensionFieldChanged) {
        dispatch(loadCompanyDataAsync());
      }
    });
  };

  const createOrUpdateProject = async (values: TimeReportingProjectFormTypes.Create) => {
    const mappedProject = mappedProjectInsertOrUpdate(values);

    if (isCreate) {
      return timeReportingProjectService.createTimeReportingProject(mappedProject, dispatch).then((res) => res.data);
    } else {
      const id = timeReportingProject?.id as string;

      const projectToUpdate = {
        ...mappedProject,
        id: id,
      } as ApiModel.TimeReportingProjectPut;

      return timeReportingProjectService
        .updateTimeReportingProject(id, projectToUpdate, dispatch)
        .then((res) => res.data);
    }
  };

  const isHourlyDateBetweenTodayAndNewCurrentDate = (newCurrentDate: Date, hourlyRateDate: Date) =>
    DateHelper.isBefore(new Date(hourlyRateDate), new Date()) &&
    DateHelper.isBefore(new Date(newCurrentDate), new Date(hourlyRateDate));

  const mappedProjectInsertOrUpdate = (
    values: TimeReportingProjectFormTypes.Create
  ): ApiModel.TimeReportingProjectPost => {
    const relations = [...(values.projectCategoryMembers ?? [])];

    relations.forEach((relation) => {
      const newCurrentDate = relation.hourlyRates?.find(
        (hr) => !hr.id && hr.fromDate && DateHelper.isBefore(new Date(hr.fromDate), new Date())
      )?.fromDate;

      if (newCurrentDate) {
        const filteredHourlyRates =
          relation.hourlyRates?.filter((hr) =>
            hr.fromDate && newCurrentDate
              ? !isHourlyDateBetweenTodayAndNewCurrentDate(newCurrentDate, hr.fromDate) &&
                (DateHelper.isEqual(new Date(newCurrentDate), new Date(hr.fromDate)) ? !hr.id : true)
              : true
          ) ?? [];
        relation.hourlyRates = [...filteredHourlyRates];
      }
    });

    // Resolve each self-billing rate against the current projectCategories so we never
    // send a stale categoryName or an unresolvable id=0 reference. The backend rejects
    // rates with timeReportingProjectCategoryId === 0 unless categoryName matches an
    // in-request category (see SelfBillingRateItem on the API).
    const categoriesById = new Map<number, TimeReportingProjectFormTypes.ProjectCategory>(
      values.projectCategories?.filter((c) => c.id !== undefined && c.id !== 0).map((c) => [c.id as number, c]) ?? []
    );
    const categoriesByName = new Map<string, TimeReportingProjectFormTypes.ProjectCategory>(
      values.projectCategories?.map((c) => [c.name, c]) ?? []
    );

    const toApiSelfBillingRate = (
      rate: TimeReportingProjectFormTypes.SelfBillingRate
    ): ApiModel.SelfBillingRateItem | null => {
      const resolved =
        (rate.timeReportingProjectCategoryId ? categoriesById.get(rate.timeReportingProjectCategoryId) : undefined) ??
        categoriesByName.get(rate.categoryName);
      if (!resolved) return null;
      return {
        id: rate.id,
        timeReportingProjectCategoryId: resolved.id ?? 0,
        categoryName: resolved.name,
        amount: rate.amount ?? 0,
        fromDate: rate.fromDate,
        isCurrent: rate.isCurrent ?? false,
      };
    };

    const mappedProjectMembers = values.projectMembers?.map((member) => {
      const {
        selfBillingHourlyRates,
        selfBillingAllHourlyRates,
        selfBillingFutureRates,
        selfBillingChangesApplyFrom,
        ...rest
      } = member;

      // A member's rates are a history now, so the whole history goes back — not just the row
      // the editable table happens to show. Rows the user never touched are sent unchanged so
      // the server's id-based merge leaves them alone; omitting one would delete it.
      let outgoing: TimeReportingProjectFormTypes.SelfBillingRate[];
      const history = selfBillingAllHourlyRates ?? [];
      if (member.isNew || history.length === 0) {
        outgoing = selfBillingHourlyRates ?? [];
      } else {
        const queued = selfBillingFutureRates ?? [];
        const editsById = new Map(queued.filter((r) => r.id).map((r) => [r.id as number, r]));
        const withEdits = history.map((r) => (r.id ? (editsById.get(r.id) ?? r) : r));
        const newlyQueued = queued.filter((r) => !r.id);
        // A visible row without an id matches no rate the API has stored: either its category was
        // assigned in this session, or the category's rates all start in the future so none was in
        // force. Both are a first rate rather than a change, and carry no date.
        const firstRates = (selfBillingHourlyRates ?? []).filter((r) => r.id === undefined);
        outgoing = [...withEdits, ...newlyQueued, ...firstRates];
      }

      const reconciledRates = outgoing
        .map(toApiSelfBillingRate)
        .filter((r): r is ApiModel.SelfBillingRateItem => r !== null);

      return {
        ...rest,
        selfBillingEnabled: member.selfBillingEnabled ?? false,
        selfBillingHourlyRates: reconciledRates,
      };
    });

    return {
      ...values,
      name: values.projectName,
      description: values.projectDescription,
      invoiceReferencePersonId: values.invoiceReferenceId,
      projectManagerPersonId: values.projectManagerId,
      projectMembers: mappedProjectMembers,
      startDate: values.projectStartDate,
      endDate: values.projectEndDate,
      invoiceTimeReportDetailLevel: values.isBundledInvoice
        ? ApiModel.InvoiceTimeReportDetailLevel.Bundled
        : values.invoiceTimeReportDetailLevel,
      hasSellerDimensionInInvoice: values.hasSellerDimensionInInvoice,
      projectCategories: values.projectCategories,
      projectCategoryMembers: relations,
    } as ApiModel.TimeReportingProjectPost;
  };

  const onProjectNameChangeHandler = (
    name: string,
    updatedValue: any,
    formikProps: FormikProps<TimeReportingProjectFormTypes.Create>,
    userInput?: boolean | undefined
  ): void => {
    const { setFieldValue } = formikProps;

    setFieldValue(FormFieldConstants.TimeReportingProject.Create.DimensionReadonly, updatedValue);

    if (updatedValue && updatedValue !== originalProjectName) {
      selectedTimeReportingProjectChangeHandler(updatedValue ?? "");
      timeReportingProjectService.isProjectNameDuplicate(updatedValue).then((res) => {
        setIsProjectNameUnique(!res.data);
      });
    } else {
      setIsProjectNameUnique(true);
    }
  };

  const onCurrencyChangeHandler = (
    name: string,
    updatedValue: any,
    formikProps: FormikProps<TimeReportingProjectFormTypes.Create>,
    userInput?: boolean | undefined
  ): void => {
    if (updatedValue) {
      setCurrency(updatedValue);
    }
  };

  const onProjectMemberSubmitForm = async (projectMember: TimeReportingProjectFormTypes.ProjectMember) => {
    const values: TimeReportingProjectFormTypes.Create = formRef.current?.getValues && formRef.current?.getValues();
    const projectCategoryMembers: ApiModel.ProjectCategoryMemberRelation[] = [...(values.projectCategoryMembers ?? [])];

    const copySubmittedProjectMember = projectMember;
    copySubmittedProjectMember.startDate = DateHelper.resetTime(projectMember.startDate) as Date;
    copySubmittedProjectMember.endDate = DateHelper.resetTime(projectMember.endDate) as Date;

    const isActive = isMemberActive(
      values.projectStartDate,
      values.projectEndDate,
      projectMember.startDate,
      projectMember.endDate
    );
    copySubmittedProjectMember.isTimeReportingActive = isActive;

    const selectedCategories = projectMember.categories?.filter((sc) => sc.selected);
    if (projectMember.isNew ?? true) {
      addMissingRelations(projectMember, projectCategoryMembers, selectedCategories, true);
    } else {
      const missingCategories = selectedCategories?.filter(
        (sc) =>
          !projectCategoryMembers.some(
            (pcm) => pcm.projectMember?.id === projectMember.id && pcm.projectCategory?.name === sc.name
          )
      );
      updateCategoryMemberRelationsByProjectMember(projectMember, projectCategoryMembers);
      addMissingRelations(projectMember, projectCategoryMembers, missingCategories, true);
    }

    formRef.current?.setFieldValue &&
      formRef.current.setFieldValue(
        FormFieldConstants.TimeReportingProject.Create.ProjectCategoryMembers,
        projectCategoryMembers
      );

    return { ...copySubmittedProjectMember, isNew: false };
  };

  const onCategorySubmitForm = async (
    category: TimeReportingProjectFormTypes.ProjectCategory,
    previousName?: string
  ) => {
    const values: TimeReportingProjectFormTypes.Create = formRef.current?.getValues && formRef.current?.getValues();
    const projectCategoryMembers: ApiModel.ProjectCategoryMemberRelation[] = [...(values.projectCategoryMembers ?? [])];

    const selectedMembers = category.categoryMembers?.filter((mr) => mr.selected);
    if (category.isNew ?? true) {
      addMissingRelations(category, projectCategoryMembers, selectedMembers, false, true);
    } else {
      const missingMembers = selectedMembers?.filter(
        (sm) =>
          !projectCategoryMembers.some(
            (pcm) => pcm.projectMember?.id === sm.id && pcm.projectCategory?.name === previousName
          )
      );
      updateCategoryMemberRelationsByCategory(category, projectCategoryMembers, previousName);
      addMissingRelations(category, projectCategoryMembers, missingMembers, false, true);
    }
    formRef.current?.setFieldValue &&
      formRef.current.setFieldValue(
        FormFieldConstants.TimeReportingProject.Create.ProjectCategoryMembers,
        projectCategoryMembers
      );

    if (previousName && previousName !== category.name) {
      // The rename has to reach every list a rate can sit in, not just the visible one — a stale
      // name in the history or the queued changes is sent back to the API and shown to the user.
      const rename = (rates?: TimeReportingProjectFormTypes.SelfBillingRate[]) =>
        rates?.map((rate) => (rate.categoryName === previousName ? { ...rate, categoryName: category.name } : rate));
      const renamedProjectMembers = values.projectMembers?.map((member) => ({
        ...member,
        selfBillingHourlyRates: rename(member.selfBillingHourlyRates),
        selfBillingAllHourlyRates: rename(member.selfBillingAllHourlyRates),
        selfBillingFutureRates: rename(member.selfBillingFutureRates),
      }));
      formRef.current?.setFieldValue &&
        formRef.current.setFieldValue(
          FormFieldConstants.TimeReportingProject.Create.ProjectMembers,
          renamedProjectMembers
        );
    }

    return { ...category, isNew: false, billingModel: ApiModel.BillingModelType.Individual };
  };

  const updateCategoryMemberRelationsByProjectMember = (
    projectMember: TimeReportingProjectFormTypes.ProjectMember,
    projectCategoryMembers: ApiModel.ProjectCategoryMemberRelation[]
  ) => {
    projectCategoryMembers.forEach((pcm) => {
      // eslint-disable-next-line eqeqeq --- supressed to avoid regression testing if changed
      if (pcm.projectMember?.id == projectMember.id) {
        const category = projectMember.categories?.find((c) => c.name === pcm.projectCategory?.name);
        const hourlyRates: ApiModel.MemberCategoryHourlyRate[] = [];

        calculateHourlyRates(
          pcm,
          hourlyRates,
          projectMember.futureCategoryRates ?? [],
          category?.hourlyRate,
          (rate: TimeReportingProjectFormTypes.FutureCategoryRate | TimeReportingProjectFormTypes.FutureMemberRate) =>
            rate.name === category?.name
        );
        if (category?.individualBillingModel !== undefined) {
          pcm.individualBillingModel = category?.individualBillingModel;
        }
        pcm.endDate = category?.accessUntil;
        pcm.normalDebitHours = category?.hours;
        pcm.hourlyRates = hourlyRates;
      }
    });
  };

  const updateCategoryMemberRelationsByCategory = (
    category: TimeReportingProjectFormTypes.ProjectCategory,
    projectCategoryMembers: ApiModel.ProjectCategoryMemberRelation[],
    previousName?: string
  ) => {
    projectCategoryMembers?.forEach((pcm) => {
      if (pcm.projectCategory && pcm.projectCategory?.name === previousName) {
        const categoryMember = category.categoryMembers?.find((cm) => cm.id === pcm.projectMember?.id);
        const hourlyRates: ApiModel.MemberCategoryHourlyRate[] = [];

        calculateHourlyRates(
          pcm,
          hourlyRates,
          category.futureMemberRates ?? [],
          categoryMember?.hourlyRate,
          (rate: TimeReportingProjectFormTypes.FutureCategoryRate | TimeReportingProjectFormTypes.FutureMemberRate) =>
            rate.id === categoryMember?.id
        );

        if (categoryMember?.individualBillingModel !== undefined) {
          pcm.individualBillingModel = categoryMember?.individualBillingModel;
        }
        pcm.endDate = categoryMember?.accessUntil;
        pcm.normalDebitHours = categoryMember?.hours;
        pcm.projectCategory = { ...pcm.projectCategory, name: category.name };
        pcm.hourlyRates = hourlyRates;
      }
    });
  };

  const calculateHourlyRates = (
    pcm: ApiModel.ProjectCategoryMemberRelation,
    hourlyRates: ApiModel.MemberCategoryHourlyRate[],
    futureRates: TimeReportingProjectFormTypes.FutureCategoryRate[] | TimeReportingProjectFormTypes.FutureMemberRate[],
    hourlyRate?: number,
    filterFunc?: (
      rate: TimeReportingProjectFormTypes.FutureCategoryRate | TimeReportingProjectFormTypes.FutureMemberRate
    ) => boolean
  ) => {
    pcm.hourlyRates?.forEach((hr) => {
      if (hr.isCurrent || !hr.fromDate || (DateHelper.isBefore(new Date(hr.fromDate), new Date()) && hr.id)) {
        hourlyRates.push(hr);
      }
    });

    futureRates
      ?.filter((fr) => filterFunc && filterFunc(fr))
      .forEach((fr) => {
        hourlyRates.push({
          id: fr.hourlyRateId,
          fromDate: fr.validFrom,
          hourlyRate: fr.hourlyRate,
          isCurrent: false,
        } as ApiModel.MemberCategoryHourlyRate);
      });
  };

  const addMissingRelations = (
    setting: TimeReportingProjectFormTypes.ProjectMember | TimeReportingProjectFormTypes.ProjectCategory,
    relations: ApiModel.ProjectCategoryMemberRelation[],
    selectedRows?:
      | TimeReportingProjectFormTypes.ExtendedProjectCategory[]
      | TimeReportingProjectFormTypes.CategoryMember[],
    explicitMember?: boolean,
    explicitCategory?: boolean
  ) => {
    selectedRows?.forEach((sr) => {
      const relation: ApiModel.ProjectCategoryMemberRelation = {
        id: undefined,
        projectMember: (explicitMember ? setting : sr) as ApiModel.ProjectMember,
        projectCategory: (explicitCategory ? setting : sr) as ApiModel.ProjectCategory,
        individualBillingModel: sr.individualBillingModel ?? 0,
        hourlyRates: [{ hourlyRate: sr.hourlyRate ?? 0, isCurrent: true }],
        normalDebitHours: sr.hours,
      };
      relations?.push(relation);
    });
  };

  const getProjectCategoriesForMember = (member: TimeReportingProjectFormTypes.ProjectMember) => {
    const values: TimeReportingProjectFormTypes.Create = formRef.current?.getValues && formRef.current?.getValues();

    const relationsForMember =
      values.projectCategoryMembers?.filter(
        (relation) =>
          // eslint-disable-next-line eqeqeq --- supressed to avoid regression testing if changed
          relation.projectMember?.id == member.id &&
          (relation.endDate ? DateHelper.isBefore(DateHelper.today(), new Date(relation?.endDate)) : true)
      ) ?? [];

    let categories = [
      LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReportingProject.NoCategories),
    ];
    if (relationsForMember?.length > 0) {
      categories = relationsForMember.map((relation) => " " + relation.projectCategory?.name);
    }

    return categories;
  };

  const getProjectCategoryDetailView = (category: TimeReportingProjectFormTypes.ProjectCategory) => {
    const values: TimeReportingProjectFormTypes.Create = formRef.current?.getValues && formRef.current?.getValues();
    return (
      <NumberOfProjectCategoryMembers
        timeReportingProject={values}
        currentCategory={category as ApiModel.ProjectCategory}
      ></NumberOfProjectCategoryMembers>
    );
  };

  const { showPreviewDialog } = useCreateCustomerDialog();

  return (
    <TimeReportingProjectForm
      ref={formRef}
      {...props}
      customerId={selectedCustomer}
      validationSchema={validationSchema}
      defaultValues={initialValues}
      handleSubmit={handleSubmit}
      onCustomerAdd={onCustomerAdd}
      onCustomerSelected={onCustomerSelectedFromForm}
      onAddReferencePerson={onAddReferencePerson}
      topCustomers={topCustomers}
      topReferencePersons={topReferencePersons}
      onIsInternalProjectChange={onIsInternalProjectChange}
      invoiceTimeReportDetailLevels={getInvoiceTimeReportDetailLevels()}
      onDuplicateCheck={onProjectNameChangeHandler}
      currency={currency}
      onCurrencyChange={onCurrencyChangeHandler}
      isCreate={isCreate}
      onCategorySubmitForm={onCategorySubmitForm}
      onProjectMemberSubmitForm={onProjectMemberSubmitForm}
      getProjectCategoriesForMember={getProjectCategoriesForMember}
      getProjectCategoryDetailView={getProjectCategoryDetailView}
      isSalaryDeviationActive={isSalaryDeviationsAvailable}
      otherDeviationTypes={enabledOtherDeviationTypes}
    />
  );
};

export default forwardRef(TimeReportingProjectFormContainer);
